import { Alert, Icon, IconButton, Skeleton, TextInput, Typography } from "@/components/atoms";
import { useNavigate } from "react-router-dom";
import { usePricingProfileContext } from "./PricingProfileContext";
import { PricingMethodCard } from "../../common/PricingMethodCard";
import { CompaniesAttachedTable } from "./CompaniesAttachedTable";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useEditPricingProfileMutation } from "@/redux/apis/config/trip/tripCost";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { addToast, clsx } from "@/utils";
import { useGetCompaniesQuery } from "@/redux/apis/company/companyApi";
import { companiesSelector } from "@/redux/slices/company/selectors";
import { useAppSelector } from "@/redux/hooks";
import { isEmpty } from "ramda";

export const PricingProfilePanel = () => {
  const navigate = useNavigate();
  const { pricingProfile } = usePricingProfileContext();
  const [isEditMode, setIsEditMode] = useState(false);

  const [editPricingProfile, { isLoading }] = useEditPricingProfileMutation();
  const {
    register,
    handleSubmit,
    formState: { isDirty },
  } = useForm<{ name: string }>({
    defaultValues: { name: pricingProfile ? pricingProfile.name : "" },
  });

  const { isFetching } = useGetCompaniesQuery(
    { pricing_profile_uuid: pricingProfile?.id ?? "", page_size: "100" },
    { skip: !pricingProfile, refetchOnMountOrArgChange: true }
  );
  const companies = useAppSelector(companiesSelector);

  const onSubmit = handleSubmit((data) => {
    if (pricingProfile && isDirty) {
      editPricingProfile({ id: pricingProfile.id, name: data.name })
        .unwrap()
        .then(() => {
          addToast("success", "Successfully Updated Pricing Profile");
          setIsEditMode(false);
        })
        .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
    } else setIsEditMode(false);
  });

  if (isLoading) return <Skeleton />;

  return (
    <div className="flex flex-col">
      {(!isFetching && isEmpty(companies)) && <Alert type="danger" message="Looks like this pricing profile is inactive right now. Attach a company to get it up and running." className="mb-4" />}
      {isEditMode ? (
        <form className="flex flex-row items-center gap-x-1.5" onSubmit={onSubmit}>
          <TextInput size="md" placeholder="Enter Pricing Profile Name" className="flex-1" maxLength={65} required {...register("name")} />
          <IconButton variant="custom" iconName="CloseCircle" iconSize="lg" iconVariant="Bold" className="text-neutral-dark-gray" onClick={() => setIsEditMode(false)} />
          <IconButton type="submit" variant="custom" iconName="TickCircle" iconSize="lg" iconVariant="Bold" className="text-success" />
        </form>
      ) : (
        <div className="flex flex-row items-center gap-2">
          <Typography variant="h2">{pricingProfile?.name}</Typography>
          <IconButton variant="custom" iconName="Edit2" iconSize="lg" onClick={() => setIsEditMode(true)} className="text-neutral-dark-gray" />
        </div>
      )}
      <div className={clsx("flex flex-col", { "opacity-25": isEditMode })}>
        <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">For Contract Pricing Profiles, RideMinder applies pricing options in this order (1 → 2)</Typography>
        <Typography variant="paragraph" className="mt-1">
          If neither is configured, the system will use 
          <span className="text-info cursor-pointer" onClick={() => navigate("../../custom-rates")}> Custom Rates </span> 
          first, then fall back to the 
          <span className="text-info cursor-pointer" onClick={() => navigate("../../default-rates")}> Default Rates</span>
        </Typography>
      </div>
      <div className={clsx("flex flex-col md:flex-row gap-5 mt-6", { "opacity-25": isEditMode })}>
        <PricingMethodCard
          title="Zone Rates" 
          description="Set prices for specific areas like airports, business districts or special operating hubs"
          icon={<Typography variant="h1" className="text-neutral-dark-gray">1</Typography>}
          onConfigure={() => navigate("zone-rates")}
          warning={pricingProfile?.hasLocationPricing ? undefined : "Pricing not set"}
        >
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row items-center rounded-lg bg-neutral-surface-gray gap-2 px-2 py-1.5 text-neutral-dark-gray">
              <Icon name="InfoCircle" size="lg" className="flex-shrink-0" />
              <Typography variant="paragraph">This pricing applies when a pickup falls within your defined zones</Typography>
            </div>
          </div>
        </PricingMethodCard>
        <PricingMethodCard
          title="State Rates" 
          description="Set prices for an entire state."
          icon={<Typography variant="h1" className="text-neutral-dark-gray">2</Typography>}
          onConfigure={() => navigate("state-rates")}
          warning={pricingProfile?.hasStatePricing ? undefined : "Pricing not set"}
        >
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row items-center rounded-lg bg-neutral-surface-gray gap-2 px-2 py-1.5 text-neutral-dark-gray">
              <Icon name="InfoCircle" size="lg" className="flex-shrink-0" />
              <Typography variant="paragraph">This pricing applies when the pickup falls within the state</Typography>
            </div>
          </div>
        </PricingMethodCard>
      </div>
      {isFetching ? <Skeleton /> : <CompaniesAttachedTable companies={companies} profile={pricingProfile} className={clsx("mt-8", { "opacity-25": isEditMode })} />}
    </div>
  );
};